// src/utils/reportService.js

import axios from 'axios';
import { API_BASE_URL } from './config';
import moment from 'moment';

const convertBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};


const EQUIPO_CODE_MAP = {
    100: 'DRONE',
    200: 'HELICOPTERO',
    300: 'AVIONETA',
};

const extractValor = (v) => {
    if (v === undefined || v === null) return v;
    if (typeof v === 'object' && !Array.isArray(v)) {
        if (Object.prototype.hasOwnProperty.call(v, 'valor')) return v.valor;
        if (Object.prototype.hasOwnProperty.call(v, 'value')) return v.value;
    }
    return v;
};

const toEquipoLabel = (val) => {
    if (val === undefined || val === null) return null;
    const raw = extractValor(val);
    if (raw === undefined || raw === null) return null;

    // Si viene como arreglo, tomar el primero
    const first = Array.isArray(raw) ? raw[0] : raw;

    // Si viene como número/código (o string numérico), mapear
    const asNum = typeof first === 'number' ? first : (typeof first === 'string' && /^\d+$/.test(first.trim()) ? Number(first.trim()) : null);
    if (asNum !== null && EQUIPO_CODE_MAP[asNum]) return EQUIPO_CODE_MAP[asNum];

    // Si ya es texto
    if (typeof first === 'string' && first.trim() !== '') return first.trim();

    return null;
};


const normalizeTextForCompare = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toUpperCase();

const normalizeApsMaterialType = (value) => {
    const text = normalizeTextForCompare(extractValor(value));
    if (!text) return null;
    if (text.includes('SOLIDO') || text.includes('SOLID') || text.includes('KG') || text.includes('KILO')) return 'SOLIDO';
    if (text.includes('LIQUIDO') || text.includes('LIQUID') || text.includes('L/HA') || text === 'L') return 'LIQUIDO';
    return null;
};

const extractUnitHintsFromIndicador = (indicador, acc = []) => {
    const value = extractValor(indicador);
    if (value === undefined || value === null) return acc;
    if (Array.isArray(value)) {
        value.forEach((item) => extractUnitHintsFromIndicador(item, acc));
        return acc;
    }
    if (typeof value === 'object') {
        Object.values(value).forEach((item) => extractUnitHintsFromIndicador(item, acc));
        return acc;
    }
    acc.push(value);
    return acc;
};

const resolveApsMaterialTypeForReport = (indicadores = {}) => {
    const idCandidates = [
        indicadores?.ID_ANALISIS,
        indicadores?.idAnalisis,
        indicadores?.id_analisis,
        indicadores?._id,
        localStorage.getItem('aplicacionesAereas_manual_id')
    ].map(extractValor).filter(Boolean);

    const directCandidates = [
        indicadores?.formulation,
        indicadores?.formulacion,
        indicadores?.formulación,
        indicadores?.tipoProducto,
        indicadores?.tipo_producto,
        indicadores?.tipoProductoAps,
        indicadores?.productType,
        indicadores?.product_type,
        indicadores?.FORMULACION,
        indicadores?.TIPO_PRODUCTO,
        indicadores?.__apsMaterialType,
    ];

    for (const candidate of directCandidates) {
        const normalized = normalizeApsMaterialType(candidate);
        if (normalized) return normalized;
    }

    const storedCandidates = [];
    idCandidates.forEach((id) => storedCandidates.push(localStorage.getItem(`aplicacionesAereas_material_type_${id}`)));
    storedCandidates.push(localStorage.getItem('aplicacionesAereas_material_type'));

    for (const candidate of storedCandidates) {
        const normalized = normalizeApsMaterialType(candidate);
        if (normalized) return normalized;
    }

    const unitHints = [];
    [
        'unidadDosisReal',
        'unidadDosisTeorica',
        'unidadDiferenciaDosis',
        'unidadVolumenMezcla',
        'unidadDosisProducto',
        'dosisProducto',
        'dosisReal',
        'dosisTeorica',
        'diferenciaDosisTeoricaReal',
        'volumenPromedio',
        'dosisTeoricasLista',
        'dosisRealesLista',
        'UNIDADES',
        'unidades',
    ].forEach((key) => extractUnitHintsFromIndicador(indicadores?.[key], unitHints));

    for (const hint of unitHints) {
        const normalized = normalizeApsMaterialType(hint);
        if (normalized) return normalized;
    }

    return 'LIQUIDO';
};


const resolveCurrentAnalysisIdForReport = (indicadores = {}, fallbackId = null) => {
    const candidates = [
        fallbackId,
        indicadores?.ID_ANALISIS,
        indicadores?.idAnalisis,
        indicadores?.id_analisis,
        indicadores?.analisis_id,
        indicadores?._id,
        indicadores?.datosAnalisis?.ID_ANALISIS,
        indicadores?.datosAnalisis?.idAnalisis,
        localStorage.getItem('aplicacionesAereas_manual_id'),
        localStorage.getItem('deteccionMaleza_idReal'),
        localStorage.getItem('deteccionMaleza_idTemp'),
        localStorage.getItem('conteoPalma_idAnalisis'),
    ];

    for (const candidate of candidates) {
        const value = extractValor(candidate);
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return String(value).trim();
        }
    }

    return null;
};

const GENERATED_PDF_REPORTS_STORAGE_KEY = 'geomotica_generated_pdf_reports';

const readGeneratedPdfReports = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(GENERATED_PDF_REPORTS_STORAGE_KEY) || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};


const normalizeReportDownloadUrl = (value) => {
    if (!value) return null;
    const text = String(value).trim();
    if (!text) return null;
    if (/^https?:\/\//i.test(text)) return text;
    const base = String(API_BASE_URL || '').replace(/\/+$/, '');
    const path = text.startsWith('/') ? text : `/${text}`;
    return `${base}${path}`;
};

const persistGeneratedPdfReport = (analysisId, report) => {
    if (!analysisId || !(report?.signed_url || report?.download_url)) return;

    const storage = readGeneratedPdfReports();
    const key = String(analysisId);
    const current = Array.isArray(storage[key]) ? storage[key] : [];
    const normalizedReport = {
        file_name: report.file_name || report.gcs_path || 'reporte.pdf',
        signed_url: normalizeReportDownloadUrl(report.signed_url || report.download_url),
        download_url: normalizeReportDownloadUrl(report.download_url || report.signed_url),
        updated: report.updated || new Date().toISOString(),
        analisis_id: key,
        tipo_archivo: 'pdf',
        tipo_reporte: report.tipo_reporte || null,
        gcs_path: report.gcs_path || report.file_name || null,
        source: 'local-response-header'
    };

    const deduped = [normalizedReport, ...current]
        .filter((item) => item && (item.signed_url || item.download_url))
        .filter((item, index, arr) => {
            const itemKey = item.gcs_path || item.download_url || item.file_name || item.signed_url;
            return arr.findIndex((other) => (other.gcs_path || other.download_url || other.file_name || other.signed_url) === itemKey) === index;
        })
        .slice(0, 10);

    storage[key] = deduped;
    localStorage.setItem(GENERATED_PDF_REPORTS_STORAGE_KEY, JSON.stringify(storage));
};

const getApsReportUnits = (materialType) => {
    const isSolid = normalizeApsMaterialType(materialType) === 'SOLIDO';
    return {
        materialType: isSolid ? 'SOLIDO' : 'LIQUIDO',
        dosisPorArea: isSolid ? 'Kg/ha' : 'L/ha',
        mezcla: isSolid ? 'Kg' : 'L',
    };
};

const setIndicadorTitulo = (obj, key, titulo) => {
    if (!obj || !key) return;
    const v = obj[key];
    if (v === undefined || v === null) return;

    if (typeof v === 'object' && !Array.isArray(v)) {
        obj[key] = { ...v, titulo: titulo ?? v.titulo };
        return;
    }

    obj[key] = { titulo, valor: v };
};

// Normaliza "tiempoTotal" para reportes a formato H:MM (evita decimales tipo 1.5)
const normalizeTiempoTotalToHMM = (tiempoTotalRaw, horaInicioRaw, horaFinRaw) => {
    const pad2 = (n) => String(Math.trunc(n)).padStart(2, '0');

    const formatMinutesAsHMM = (totalMinutes) => {
        const n = Number(totalMinutes);
        if (!Number.isFinite(n)) return null;
        const min = Math.max(0, Math.round(n));
        const hh = Math.floor(min / 60);
        const mm = min % 60;
        return `${hh}:${pad2(mm)}`;
    };

    const parseHMMToMinutes = (t) => {
        const s = String(t ?? '').trim();
        if (!s) return null;
        // soporta H:MM, HH:MM y HH:MM:SS
        const m = s.match(/^(\d{1,3}):(\d{1,2})(?::(\d{2}))?$/);
        if (!m) return null;
        const hh = Number(m[1]);
        const mm = Number(m[2]);
        const ss = m[3] != null ? Number(m[3]) : 0;
        if (Number.isNaN(hh) || Number.isNaN(mm) || Number.isNaN(ss)) return null;
        let minutes = (hh * 60) + mm;
        if (ss >= 30) minutes += 1;
        return minutes;
    };

    const parseNumericToMinutes = (v) => {
        if (v === null || v === undefined) return null;
        let s = String(v).trim();
        if (!s) return null;

        if (s.includes(':')) return parseHMMToMinutes(s);

        // normalizar coma decimal y remover unidades
        s = s.replace(/,/g, '.').replace(/[^0-9.\-]/g, '');
        if (!s || s === '-' || s === '.' || s === '-.') return null;

        const n = Number(s);
        if (!Number.isFinite(n)) return null;

        const abs = Math.abs(n);
        const hasDecimal = Math.abs(n - Math.trunc(n)) > 1e-9;

        // Heurística:
        // - enteros grandes (>=60 y <=1440) => probablemente MINUTOS
        // - con decimales o valores pequeños => HORAS
        if (!hasDecimal && abs >= 60 && abs <= 24 * 60) return n;
        return n * 60;
    };

    // 1) Preferir tiempoTotal si viene
    const mins = parseNumericToMinutes(tiempoTotalRaw);
    if (mins !== null) return formatMinutesAsHMM(mins);

    // 2) Fallback: calcular desde horaInicio/horaFin
    const mIni = parseHMMToMinutes(horaInicioRaw);
    const mFin = parseHMMToMinutes(horaFinRaw);
    if (mIni === null || mFin === null) return null;

    let diff = mFin - mIni;
    if (diff < 0) diff += 24 * 60;
    return formatMinutesAsHMM(diff);
};


const normalizeApsIndicadoresForReport = (indicadores) => {
    if (!indicadores || typeof indicadores !== 'object') return indicadores;

    // Clonar para no mutar el estado/UI
    let out;
    try {
        out = JSON.parse(JSON.stringify(indicadores));
    } catch (e) {
        out = { ...indicadores };
    }



    // ---------------------------------------------------------------------
    // VRA: nombre visible del subanálisis interno TASA_VARIABLE.
    // Mantener una marca explícita para que el PDF use exclusivamente la
    // plantilla especializada sin alterar Aplicaciones Aéreas normales.
    // ---------------------------------------------------------------------
    const tipoSubAnalisisReporte = extractValor(
        out?.tipoSubAnalisis ?? out?.TIPO_SUB_ANALISIS ?? out?.tipo_sub_analisis
    );
    out.__isVraReport = String(tipoSubAnalisisReporte || '')
        .trim()
        .toUpperCase() === 'TASA_VARIABLE';
    out.__apsVisibleMode = out.__isVraReport ? 'VRA' : 'NORMAL';

    // ---------------------------------------------------------------------
    // 0) TIPO DE PRODUCTO APS: dejarlo explícito para que el PDF use
    //    Kg/Kg/ha en sólido y L/L/ha en líquido.
    // ---------------------------------------------------------------------
    const apsMaterialType = resolveApsMaterialTypeForReport(out);
    const apsUnits = getApsReportUnits(apsMaterialType);

    out.__apsMaterialType = apsUnits.materialType;
    out.formulation = apsUnits.materialType;
    out.formulacion = apsUnits.materialType;
    out.tipoProducto = apsUnits.materialType;
    out.tipo_producto = apsUnits.materialType;
    out.TIPO_PRODUCTO = { titulo: 'Tipo de producto', valor: apsUnits.materialType };
    out.FORMULACION = { titulo: 'Formulación', valor: apsUnits.materialType };
    out.__apsReportUnits = {
        dosisPorArea: apsUnits.dosisPorArea,
        volumenMezcla: apsUnits.mezcla,
    };
    out.UNIDADES = {
        ...(out.UNIDADES && typeof out.UNIDADES === 'object' ? out.UNIDADES : {}),
        DOSIS_PRODUCTO: apsUnits.dosisPorArea,
        DOSIS_REAL: apsUnits.dosisPorArea,
        DOSIS_TEORICA: apsUnits.dosisPorArea,
        DIFERENCIA_DOSIS: apsUnits.dosisPorArea,
        VOLUMEN_MEZCLA: apsUnits.mezcla,
        VOLUMEN_AGUA: 'L',
    };
    out.unidades = {
        ...(out.unidades && typeof out.unidades === 'object' ? out.unidades : {}),
        dosisProducto: apsUnits.dosisPorArea,
        dosisReal: apsUnits.dosisPorArea,
        dosisTeorica: apsUnits.dosisPorArea,
        diferenciaDosis: apsUnits.dosisPorArea,
        volumenMezcla: apsUnits.mezcla,
        volumenAgua: 'L',
    };

    // ---------------------------------------------------------------------
    // 1) TIEMPO TOTAL: normalizar a H:MM (evita decimales tipo 1.5 en PDF)
    // ---------------------------------------------------------------------
    const ttRaw = extractValor(out?.tiempoTotal ?? out?.TIEMPO_TOTAL ?? out?.tiempo_total);
    const hIniRaw = extractValor(out?.horaInicio ?? out?.HORA_INICIO ?? out?.hora_inicio);
    const hFinRaw = extractValor(out?.horaFin ?? out?.HORA_FIN ?? out?.hora_fin);

    const ttFmt = normalizeTiempoTotalToHMM(ttRaw, hIniRaw, hFinRaw);
    if (ttFmt) {
        if (out.tiempoTotal && typeof out.tiempoTotal === 'object' && !Array.isArray(out.tiempoTotal)) {
            out.tiempoTotal = { ...out.tiempoTotal, valor: ttFmt };
        } else {
            out.tiempoTotal = ttFmt;
        }
    }
// ---------------------------------------------------------------------
    // 1) EQUIPOS: asegurar texto (ej: DRONE) para el reporte
    // ---------------------------------------------------------------------
    const equipoLabel =
        toEquipoLabel(out?.equiposLista) ||
        toEquipoLabel(out?.equipos) ||
        toEquipoLabel(out?.EQUIPOS) ||
        toEquipoLabel(out?.equipo) ||
        toEquipoLabel(out?.EQUIPO);

    if (equipoLabel) {
        // Campo explícito para el template
        out.equiposTexto = equipoLabel;
        out.EQUIPOS_TEXTO = equipoLabel;

        // Si existen estos campos, dejarlos consistentes
        if (out.equiposLista && typeof out.equiposLista === 'object' && !Array.isArray(out.equiposLista)) {
            const valor = extractValor(out.equiposLista);
            if (Array.isArray(valor)) {
                out.equiposLista = { ...out.equiposLista, valor: [equipoLabel] };
            } else {
                out.equiposLista = { ...out.equiposLista, valor: equipoLabel };
            }
        }

        // Crear/forzar un campo EQUIPOS que el template pueda leer
        out.EQUIPOS = { titulo: 'EQUIPOS', valor: equipoLabel };
    }

    // ---------------------------------------------------------------------
    // 2) Labels por lote: evitar "KML 1" y usar IDs/LOTE si están disponibles
    // ---------------------------------------------------------------------
    const lotesLabels =
        (out?.lotesLista && Array.isArray(out.lotesLista?.valor) ? out.lotesLista.valor : null) ||
        (Array.isArray(out?.lotesLista) ? out.lotesLista : null);

    if (lotesLabels && lotesLabels.length > 0) {
        // Intentar reemplazar listadoKmls (cuando el template lo usa como labels)
        if (out.listadoKmls && typeof out.listadoKmls === 'object' && !Array.isArray(out.listadoKmls)) {
            out.listadoKmls = {
                ...out.listadoKmls,
                titulo: 'Lotes',
                raw: lotesLabels,
                valor: lotesLabels.join(', '),
            };
        } else {
            out.listadoKmls = {
                titulo: 'Lotes',
                raw: lotesLabels,
                valor: lotesLabels.join(', '),
            };
        }

        // También actualizar numeroKmls si existe
        if (out.numeroKmls && typeof out.numeroKmls === 'object' && !Array.isArray(out.numeroKmls)) {
            out.numeroKmls = { ...out.numeroKmls, valor: lotesLabels.length };
        }
    }

    // ---------------------------------------------------------------------
    // 3) Títulos/orden del resumen (para que el template quede como en UI)
    // ---------------------------------------------------------------------
    out.resumenTitulo = 'Resumen';
    out.resumenPorLoteTitulo = 'Resumen';

    setIndicadorTitulo(out, 'velocidadAps', 'Velocidad');
    setIndicadorTitulo(out, 'alturaAps', 'Altura');
    setIndicadorTitulo(out, 'volumenAgua', 'Volumen de agua');
    setIndicadorTitulo(out, 'dosisProducto', 'Dosis de producto');
    setIndicadorTitulo(out, 'dosisReal', 'Volumen real');
    setIndicadorTitulo(out, 'dosisTeorica', 'Volumen teórico');
    setIndicadorTitulo(out, 'volumenPromedio', 'Volumen mezcla');
    setIndicadorTitulo(out, 'diferenciaDosisTeoricaReal', 'Dif. volumen T vs R');
    setIndicadorTitulo(out, 'tiempoTotal', 'Tiempo total');

    // Array explícito (si el template lo soporta / para compatibilidad futura)
    const cardKeys = [
        'velocidadAps',
        'alturaAps',
        'volumenAgua',
        'dosisProducto',
        'dosisReal',
        'dosisTeorica',
        'volumenPromedio',
        'diferenciaDosisTeoricaReal',
        'tiempoTotal',
    ];

    out.resumenCards = cardKeys
        .filter((k) => out[k] !== undefined && out[k] !== null)
        .map((k) => ({
            key: k,
            titulo: out[k]?.titulo ?? k,
            valor: out[k]?.valor ?? out[k],
        }));

    // Reordenar propiedades principales al inicio (por si el template hace Object.keys())
    const preferredOrder = ['analisis', ...cardKeys];
    const ordered = {};

    preferredOrder.forEach((k) => {
        if (out[k] !== undefined) ordered[k] = out[k];
    });

    Object.keys(out).forEach((k) => {
        if (ordered[k] === undefined) ordered[k] = out[k];
    });

    return ordered;
};

const normalizeIndicadoresForReport = (indicadores) => {
    if (!indicadores || typeof indicadores !== 'object') return indicadores;
    if (indicadores.analisis === 'APLICACIONES_AEREAS') return normalizeApsIndicadoresForReport(indicadores);
    // Herbicidas usa la misma plantilla/indicadores que APS (con sus propias unidades).
    if (indicadores.analisis === 'HERBICIDAS') return normalizeApsIndicadoresForReport(indicadores);
    return indicadores;
};

// Arma lo que recibe /reporteria (y el PDF automático del análisis en
// segundo plano): mismas imágenes, indicadores normalizados y gráficas.
export const construirPayloadReporte = async (imgLaflet, indicadores, idUsuario, logo, labelsAndColors, comment, tipoMapeo, chartsImages = [], northWestCoords, southEastCoords, coordinateSystem, idAnalisisActual = null, zoomImages = []) => {
    const indicadoresForReport = normalizeIndicadoresForReport(indicadores);
    console.log("indicadores normalizados para reporte:", indicadoresForReport);

    let logoBase64 = logo;
    if (logo && typeof logo === 'string' && logo.startsWith("blob:")) {
        const response = await fetch(logo);
        const blob = await response.blob();
        logoBase64 = await convertBlobToBase64(blob);
    }

    // Logo de ODDIS MAPS para el header del reporte y la marca de agua.
    // Es el PNG sin fondo de la marca, así que se carga una sola vez.
    let logoDefaultBase64 = null;
    try {
        const { default: brandLogoImg } = await import('../assets/img/logo_oddis_horizontal.png');
        const brandLogoResponse = await fetch(brandLogoImg);
        const brandLogoBlob = await brandLogoResponse.blob();
        logoDefaultBase64 = await convertBlobToBase64(brandLogoBlob);
    } catch (logoDefaultError) {
        console.warn('⚠️ Logo de marca no disponible, usando fallback:', logoDefaultError);
    }
    const watermarkBase64 = logoDefaultBase64;

    let lotesData = [];
    let datosTemp = null;
    if (indicadoresForReport?.analisis === 'DETECCION_MALEZA') {
        try {
            const storedDatosTemp = localStorage.getItem('deteccionMaleza_datos_temp');

            if (storedDatosTemp) {
                datosTemp = JSON.parse(storedDatosTemp);
            } else {
                console.warn('⚠️ No se encontraron datos temporales en localStorage');
            }

            // Obtener datos de lotes
            const storedLotesData = localStorage.getItem('deteccionMaleza_lotesData');
            if (storedLotesData) {
                const parsedData = JSON.parse(storedLotesData);

                // Manejar el nuevo formato con timestamp
                if (parsedData && typeof parsedData === 'object' && parsedData.data) {
                    lotesData = parsedData.data;
                }
            }
        } catch (error) {
            console.error('❌ Error al obtener datos de lotes:', error);
        }
    }

    // Obtener la zona horaria del usuario
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const now = moment();
    const formattedDateTime = now.format('dddd, D [de] MMMM [de] YYYY, h:mm A');
    const formattedFileDateTime = now.format('YYYYMMDD_HHmmss');

    const idAnalisisReporte = resolveCurrentAnalysisIdForReport(indicadoresForReport, idAnalisisActual);

    const payload = {
        imgData: imgLaflet,
        indicadores: indicadoresForReport,
        tipoReporte: indicadoresForReport.analisis,
        idAnalisis: idAnalisisReporte,
        ID_ANALISIS: idAnalisisReporte,
        usuarioId: idUsuario,
        logo: logoBase64,
        watermark: watermarkBase64,
        logoDefault: logoDefaultBase64,
        tipoMapeo: tipoMapeo,
        labelsAndColors: {
            title: labelsAndColors.title,
            labels: labelsAndColors.labels
        },
        comment: comment,
        currentDateTime: formattedDateTime,
        fileDateTime: formattedFileDateTime,
        timeZone: timeZone,
        chartsBase64: chartsImages,
        zoomImages: Array.isArray(zoomImages) ? zoomImages : [],
        northWestCoords: northWestCoords,
        southEastCoords: southEastCoords,
        coordinateSystem: coordinateSystem,
        formulation: indicadoresForReport.formulation || indicadoresForReport.formulacion || indicadoresForReport.__apsMaterialType,
        tipoProducto: indicadoresForReport.tipoProducto || indicadoresForReport.tipo_producto || indicadoresForReport.__apsMaterialType,
        unidades: indicadoresForReport.unidades,
        UNIDADES: indicadoresForReport.UNIDADES,
        __apsReportUnits: indicadoresForReport.__apsReportUnits,
        __isVraReport: indicadoresForReport.__isVraReport === true,
        apsVisibleMode: indicadoresForReport.__apsVisibleMode,
        lotesData: lotesData, // Agregar datos de lotes para DETECCION_MALEZA
        datosTemp: datosTemp // Agregar datos finca para DETECCION_MALEZA
    };

    // Si es reporte de CONTEO_PALMA, intentar extraer métricas visibles en la UI
    if (indicadoresForReport?.analisis === 'CONTEO_PALMA') {
        const extractFromIndicadores = (keys) => {
            for (const k of keys) {
                const v = indicadores[k];
                if (v === undefined || v === null) continue;
                if (typeof v === 'object') {
                    if (v.valor !== undefined) return v.valor;
                    if (v.value !== undefined) return v.value;
                    if (v.valor === 0) return 0;
                } else {
                    return v;
                }
            }
            return undefined;
        };

        payload.areaApplied = extractFromIndicadores(['areaAplicada','area_aplicada','totalAreaAplicada','areaApplied','area_aplicada_ha','areaAppliedHa']) ?? 'N/A';
        payload.areaVariationPercent = extractFromIndicadores(['porcentajeVariacion','areaVariationPercent','variacion_area_percent','porcentajeVariacionArea']) ?? 'N/A';
        payload.dosisVariationPercent = extractFromIndicadores(['porcentajeVariacionDosis','dosisVariationPercent','variacion_dosis_percent','porcentajeVariacionDosis']) ?? 'N/A';
        payload.conteoPalmas = extractFromIndicadores(['conteoPalmas','conteo_palmas','totalPalmas','conteo']) ?? undefined;
    }

    let reportRoute;
    if (payload.tipoReporte === 'DETECCION_MALEZA') {
        reportRoute = 'reporteria/deteccion-maleza';
    } else if (payload.tipoReporte === 'CONTEO_PALMA' || payload.tipoReporte === 'CONTEO_PALMAS') {
        reportRoute = 'reporteria/palma';
    } else {
        reportRoute = 'reporteria/mapeo';
    }

    return { payload, reportRoute, indicadoresForReport, formattedFileDateTime, idAnalisisReporte };
};

const TIEMPO_MAXIMO_PDF_MS = 3 * 60 * 1000;

export const sendDashboardData = async (imgLaflet, indicadores, idUsuario, logo, labelsAndColors, comment, tipoMapeo, chartsImages = [], northWestCoords, southEastCoords, coordinateSystem, idAnalisisActual = null, zoomImages = []) => {
    try {
        console.log("indicadores en sendDashboardData:", indicadores);

        const { payload, reportRoute, indicadoresForReport, formattedFileDateTime, idAnalisisReporte } = await construirPayloadReporte(
            imgLaflet, indicadores, idUsuario, logo, labelsAndColors, comment, tipoMapeo, chartsImages,
            northWestCoords, southEastCoords, coordinateSystem, idAnalisisActual, zoomImages
        );

        const response = await axios.post(`${API_BASE_URL}${reportRoute}`, payload, {
            responseType: 'blob',
            // Sin límite el botón se quedaba «pensando» para siempre si el servidor no respondía.
            timeout: TIEMPO_MAXIMO_PDF_MS,
        });

        if (response.status === 200) {
            const fileName = `${indicadoresForReport.analisis}_${formattedFileDateTime}.pdf`;

            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            window.URL.revokeObjectURL(url);
            link.remove(); // Limpiar el DOM

            const reportSavedHeader = response.headers?.['x-report-saved'];
            const resolvedReportAnalysisId = response.headers?.['x-report-analysis-id'] || idAnalisisReporte;
            const reportWasSaved = reportSavedHeader === 'true';
            const reportDownloadUrl = normalizeReportDownloadUrl(response.headers?.['x-report-download-url']);
            const reportSignedUrl = normalizeReportDownloadUrl(response.headers?.['x-report-signed-url']) || reportDownloadUrl;
            const reportGcsPath = response.headers?.['x-report-gcs-path'];
            const reportFileName = response.headers?.['x-report-file-name'] || reportGcsPath || fileName;

            const reportMetadata = reportSignedUrl
                ? {
                    file_name: reportFileName,
                    signed_url: reportSignedUrl,
                    download_url: reportDownloadUrl || reportSignedUrl,
                    updated: new Date().toISOString(),
                    analisis_id: resolvedReportAnalysisId,
                    tipo_archivo: 'pdf',
                    tipo_reporte: indicadoresForReport.analisis,
                    gcs_path: reportGcsPath || reportFileName
                }
                : null;

            if (reportWasSaved && resolvedReportAnalysisId) {
                try {
                    const storageKey = 'geomotica_generated_pdf_ids';
                    const current = JSON.parse(localStorage.getItem(storageKey) || '[]');
                    const next = Array.from(new Set([
                        ...(Array.isArray(current) ? current.map(String) : []),
                        String(resolvedReportAnalysisId)
                    ]));
                    localStorage.setItem(storageKey, JSON.stringify(next));

                    if (reportMetadata) {
                        persistGeneratedPdfReport(resolvedReportAnalysisId, reportMetadata);
                    }
                } catch (storageError) {
                    console.warn('No se pudo guardar el estado local del PDF generado:', storageError);
                }
            }

            const eventDetail = {
                idAnalisis: resolvedReportAnalysisId,
                tipoReporte: indicadoresForReport.analisis,
                saved: reportWasSaved,
                report: reportMetadata
            };

            window.dispatchEvent(new CustomEvent('geomotica:pdf-report-generated', { detail: eventDetail }));

            // Segundo refresh suave para dar tiempo a que el historial lea metadata/GCS en producción.
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('geomotica:pdf-report-generated', { detail: eventDetail }));
            }, 900);
        } else {
            console.error("Error al generar el reporte");
        }
    } catch (error) {
        console.error("Error al enviar los datos del dashboard:", error);
        // Quien llama avisa al usuario; antes el error se quedaba en la consola.
        throw error;
    }
};
